import React, { useState, useEffect, memo, useCallback } from "react";
import { Check, Loader } from "lucide-react";

// Use memo to prevent unnecessary re-renders
const ChargesConfiguration = memo(
  ({
    activeTab,
    onGSTSave,
    onCustomChargeSave,
    branchCode,
    initialData = {},
    loading = false,
    selectedMetalTypes = [],
  }) => {
    // Use more stable state initialization to avoid re-renders
    const [gstConfig, setGstConfig] = useState({
      rate: 3,
      includesMaking: true,
      includesStone: false,
    });

    const [makingCharge, setMakingCharge] = useState({
      type: "PERCENTAGE",
      value: 10,
      enabled: true,
      slabs: [],
    });

    const [wastageCharge, setWastageCharge] = useState({
      type: "PERCENTAGE",
      value: 2,
      enabled: true,
    });

    const [hallmarkCharge, setHallmarkCharge] = useState({
      enabled: true,
      charge: 50,
    });

    const [selectedMetals, setSelectedMetals] = useState(selectedMetalTypes);
    const [showSlabConfig, setShowSlabConfig] = useState(false);
    const [slabs, setSlabs] = useState([
      { minWeight: 0, maxWeight: 10, value: 10 },
      { minWeight: 10, maxWeight: 20, value: 8 },
      { minWeight: 20, maxWeight: 50, value: 6 },
      { minWeight: 50, maxWeight: 100, value: 5 },
    ]);

    // Use useEffect with proper object comparison to update state only when necessary
    useEffect(() => {
      if (!initialData || Object.keys(initialData).length === 0) return;

      // Only update states if values have actually changed
      const gstChanged =
        initialData.gst &&
        (gstConfig.rate !== (initialData.gst.rate ?? 3) ||
          gstConfig.includesMaking !==
            (initialData.gst.includesMaking ?? true) ||
          gstConfig.includesStone !== (initialData.gst.includesStone ?? false));

      if (gstChanged && initialData.gst) {
        setGstConfig({
          rate: initialData.gst.rate ?? 3,
          includesMaking: initialData.gst.includesMaking ?? true,
          includesStone: initialData.gst.includesStone ?? false,
        });
      }

      // Similarly check if making charge config has changed
      if (initialData.making) {
        setMakingCharge((prev) => ({
          type: initialData.making.type || "PERCENTAGE",
          value: initialData.making.value ?? 10,
          enabled: initialData.making.enabled ?? true,
          slabs: initialData.making.slabs || [],
        }));

        // Update slabs if they exist
        if (initialData.making.slabs?.length > 0) {
          setSlabs(initialData.making.slabs);
        }

        // Update slab config visibility
        setShowSlabConfig(initialData.making.type === "SLAB");
      }

      // Update wastage charges if they've changed
      if (initialData.wastage) {
        setWastageCharge((prev) => ({
          type: initialData.wastage.type || "PERCENTAGE",
          value: initialData.wastage.value ?? 2,
          enabled: initialData.wastage.enabled ?? true,
        }));
      }

      // Update hallmark charges if they've changed
      if (initialData.hallmark) {
        setHallmarkCharge((prev) => ({
          enabled: initialData.hallmark.enabled ?? true,
          charge: initialData.hallmark.charge ?? 50,
        }));
      }
    }, [initialData]);

    // Update selectedMetals when selectedMetalTypes prop changes
    useEffect(() => {
      setSelectedMetals(selectedMetalTypes);
    }, [selectedMetalTypes]);

    const metalTypes = ["GOLD", "SILVER", "PLATINUM"];

    const handleMetalSelection = (metal) => {
      setSelectedMetals((prev) =>
        prev.includes(metal)
          ? prev.filter((m) => m !== metal)
          : [...prev, metal]
      );
    };

    const renderGSTConfig = () => (
      <div className="space-y-6">
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="font-semibold text-gray-900 mb-4">
            GST Configuration
          </h3>

          <div className="grid gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                GST Rate (%)
              </label>
              <input
                type="number"
                value={gstConfig.rate}
                onChange={(e) =>
                  setGstConfig((prev) => ({
                    ...prev,
                    rate: parseFloat(e.target.value) || 0,
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                step="0.1"
                min="0"
                max="100"
              />
            </div>

            <div className="space-y-3">
              <label className="flex items-center hover:bg-gray-100 p-2 rounded">
                <input
                  type="checkbox"
                  checked={gstConfig.includesMaking}
                  onChange={(e) =>
                    setGstConfig((prev) => ({
                      ...prev,
                      includesMaking: e.target.checked,
                    }))
                  }
                  className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <span className="ml-3 text-sm">
                  Apply GST on Making Charges
                </span>
              </label>

              <label className="flex items-center hover:bg-gray-100 p-2 rounded">
                <input
                  type="checkbox"
                  checked={gstConfig.includesStone}
                  onChange={(e) =>
                    setGstConfig((prev) => ({
                      ...prev,
                      includesStone: e.target.checked,
                    }))
                  }
                  className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <span className="ml-3 text-sm">Apply GST on Stone Value</span>
              </label>
            </div>

            <div className="pt-4">
              <button
                onClick={handleGSTSave}
                disabled={loading}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 
                       disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Save GST Configuration
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );

    const renderMakingCharges = () => (
      <div className="space-y-6">
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="font-semibold text-gray-900 mb-4">
            Making Charges Configuration
          </h3>

          <div className="grid gap-4">
            <div className="flex items-center gap-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={makingCharge.enabled}
                  onChange={(e) =>
                    setMakingCharge((prev) => ({
                      ...prev,
                      enabled: e.target.checked,
                    }))
                  }
                  className="h-4 w-4 text-blue-600 rounded"
                />
                <span className="ml-2 text-sm font-medium">
                  Enable Making Charges
                </span>
              </label>
            </div>

            {makingCharge.enabled && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Charge Type
                  </label>
                  <select
                    value={makingCharge.type}
                    onChange={(e) => {
                      setMakingCharge((prev) => ({
                        ...prev,
                        type: e.target.value,
                      }));
                      setShowSlabConfig(e.target.value === "SLAB");
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="PERCENTAGE">
                      Percentage of Metal Value
                    </option>
                    <option value="PER_GRAM">Fixed Per Gram</option>
                    <option value="FIXED">Fixed Amount</option>
                    <option value="SLAB">Weight-based Slabs</option>
                  </select>
                </div>

                {makingCharge.type !== "SLAB" ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Value {makingCharge.type === "PERCENTAGE" ? "(%)" : "(₹)"}
                    </label>
                    <input
                      type="number"
                      value={makingCharge.value}
                      onChange={(e) =>
                        setMakingCharge((prev) => ({
                          ...prev,
                          value: parseFloat(e.target.value) || 0,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      step="0.01"
                      min="0"
                    />
                  </div>
                ) : (
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Weight Slabs (per gram charges)
                    </label>
                    {slabs.map((slab, index) => (
                      <div key={index} className="flex gap-2 items-center">
                        <input
                          type="number"
                          value={slab.minWeight}
                          onChange={(e) => {
                            const newSlabs = [...slabs];
                            newSlabs[index].minWeight =
                              parseFloat(e.target.value) || 0;
                            setSlabs(newSlabs);
                          }}
                          className="w-24 px-2 py-1 border rounded"
                          placeholder="Min"
                        />
                        <span>-</span>
                        <input
                          type="number"
                          value={slab.maxWeight}
                          onChange={(e) => {
                            const newSlabs = [...slabs];
                            newSlabs[index].maxWeight =
                              parseFloat(e.target.value) || 0;
                            setSlabs(newSlabs);
                          }}
                          className="w-24 px-2 py-1 border rounded"
                          placeholder="Max"
                        />
                        <span>gm:</span>
                        <input
                          type="number"
                          value={slab.value}
                          onChange={(e) => {
                            const newSlabs = [...slabs];
                            newSlabs[index].value =
                              parseFloat(e.target.value) || 0;
                            setSlabs(newSlabs);
                          }}
                          className="w-24 px-2 py-1 border rounded"
                          placeholder="₹/gm"
                        />
                        <button
                          onClick={() =>
                            setSlabs(slabs.filter((_, i) => i !== index))
                          }
                          className="text-red-500 hover:text-red-700"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() =>
                        setSlabs([
                          ...slabs,
                          { minWeight: 0, maxWeight: 0, value: 0 },
                        ])
                      }
                      className="text-blue-600 hover:text-blue-700 text-sm"
                    >
                      + Add Slab
                    </button>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Apply to Metal Types
                  </label>
                  <div className="flex gap-3">
                    {metalTypes.map((metal) => (
                      <label key={metal} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={selectedMetals.includes(metal)}
                          onChange={() => handleMetalSelection(metal)}
                          className="h-4 w-4 text-blue-600 rounded"
                        />
                        <span className="ml-2 text-sm">{metal}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </>
            )}

            <div className="pt-4">
              <button
                onClick={handleMakingChargeSave}
                disabled={
                  loading ||
                  (makingCharge.enabled && selectedMetals.length === 0)
                }
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 
                       disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Save Making Charges
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );

    const renderWastageCharges = () => (
      <div className="space-y-6">
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="font-semibold text-gray-900 mb-4">
            Wastage Configuration
          </h3>

          <div className="grid gap-4">
            <div className="flex items-center gap-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={wastageCharge.enabled}
                  onChange={(e) =>
                    setWastageCharge((prev) => ({
                      ...prev,
                      enabled: e.target.checked,
                    }))
                  }
                  className="h-4 w-4 text-blue-600 rounded"
                />
                <span className="ml-2 text-sm font-medium">
                  Enable Wastage Charges
                </span>
              </label>
            </div>

            {wastageCharge.enabled && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Wastage Type
                  </label>
                  <select
                    value={wastageCharge.type}
                    onChange={(e) =>
                      setWastageCharge((prev) => ({
                        ...prev,
                        type: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="PERCENTAGE">Percentage</option>
                    <option value="FIXED">Fixed Amount</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Value {wastageCharge.type === "PERCENTAGE" ? "(%)" : "(₹)"}
                  </label>
                  <input
                    type="number"
                    value={wastageCharge.value}
                    onChange={(e) =>
                      setWastageCharge((prev) => ({
                        ...prev,
                        value: parseFloat(e.target.value) || 0,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    step="0.01"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Apply to Metal Types
                  </label>
                  <div className="flex gap-3">
                    {metalTypes.map((metal) => (
                      <label key={metal} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={selectedMetals.includes(metal)}
                          onChange={() => handleMetalSelection(metal)}
                          className="h-4 w-4 text-blue-600 rounded"
                        />
                        <span className="ml-2 text-sm">{metal}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </>
            )}

            <div className="pt-4">
              <button
                onClick={handleWastageChargeSave}
                disabled={
                  loading ||
                  (wastageCharge.enabled && selectedMetals.length === 0)
                }
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 
                       disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Save Wastage Configuration
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );

    const renderHallmarkCharges = () => (
      <div className="space-y-6">
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="font-semibold text-gray-900 mb-4">Hallmark Charges</h3>

          <div className="grid gap-4">
            <div className="flex items-center gap-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={hallmarkCharge.enabled}
                  onChange={(e) =>
                    setHallmarkCharge((prev) => ({
                      ...prev,
                      enabled: e.target.checked,
                    }))
                  }
                  className="h-4 w-4 text-blue-600 rounded"
                />
                <span className="ml-2 text-sm font-medium">
                  Enable Hallmark Charges
                </span>
              </label>
            </div>

            {hallmarkCharge.enabled && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hallmark Charge (₹ per item)
                  </label>
                  <input
                    type="number"
                    value={hallmarkCharge.charge}
                    onChange={(e) =>
                      setHallmarkCharge((prev) => ({
                        ...prev,
                        charge: parseFloat(e.target.value) || 0,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    step="1"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Apply to Metal Types
                  </label>
                  <div className="flex gap-3">
                    {metalTypes.map((metal) => (
                      <label key={metal} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={selectedMetals.includes(metal)}
                          onChange={() => handleMetalSelection(metal)}
                          className="h-4 w-4 text-blue-600 rounded"
                        />
                        <span className="ml-2 text-sm">{metal}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </>
            )}

            <div className="pt-4">
              <button
                onClick={handleHallmarkChargeSave}
                disabled={
                  loading ||
                  (hallmarkCharge.enabled && selectedMetals.length === 0)
                }
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 
                       disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Save Hallmark Charges
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );

    // For optimization, we create stable callback functions with useCallback
    const handleGSTSave = useCallback(() => {
      onGSTSave({
        ...gstConfig,
        branchCode,
      });
    }, [gstConfig, branchCode, onGSTSave]);

    const handleMakingChargeSave = useCallback(() => {
      onCustomChargeSave({
        chargeType: "making",
        configuration: {
          ...makingCharge,
          slabs: showSlabConfig ? slabs : [],
        },
        metalTypes: selectedMetals,
        branchCode,
      });
    }, [
      makingCharge,
      slabs,
      showSlabConfig,
      selectedMetals,
      branchCode,
      onCustomChargeSave,
    ]);

    const handleWastageChargeSave = useCallback(() => {
      onCustomChargeSave({
        chargeType: "wastage",
        configuration: wastageCharge,
        metalTypes: selectedMetals,
        branchCode,
      });
    }, [wastageCharge, selectedMetals, branchCode, onCustomChargeSave]);

    const handleHallmarkChargeSave = useCallback(() => {
      onCustomChargeSave({
        chargeType: "hallmark",
        configuration: hallmarkCharge,
        metalTypes: selectedMetals,
        branchCode,
      });
    }, [hallmarkCharge, selectedMetals, branchCode, onCustomChargeSave]);

    // Render based on active tab
    const renderContent = () => {
      switch (activeTab) {
        case "gst":
          return renderGSTConfig();
        case "making":
          return renderMakingCharges();
        case "wastage":
          return renderWastageCharges();
        case "hallmark":
          return renderHallmarkCharges();
        default:
          return <div>Select a configuration tab</div>;
      }
    };

    // Add a debug output in the return
    return (
      <div className="max-w-4xl">
        {/* Debug info - you can remove this in production */}

        {renderContent()}
      </div>
    );
  }
);

// Add a display name for debugging
ChargesConfiguration.displayName = "ChargesConfiguration";

export default ChargesConfiguration;
